import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
} from '@tanstack/react-table';
import LoadingSpinner from './LoadingSpinner'; // Importa lo spinner

// Funzione per recuperare i dati paginati
const fetchPaginatedPassages = async ({ queryKey }) => {
    const [_key, filters, pagination, sorting] = queryKey;

    const startDate = filters.startDate ? Math.floor(filters.startDate.setHours(0, 0, 0, 0) / 1000) : null;
    const endDate = filters.endDate ? Math.floor(filters.endDate.setHours(23, 59, 59, 999) / 1000) : null;

    const sortField = sorting.length > 0 ? sorting[0].id : 'Data e ora';
    const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'DESC' : 'ASC') : 'DESC';

    const params = {
        location: filters.location,
        passageCounterID: filters.passageCounterID,
        startDate,
        endDate,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        sortBy: sortField,
        sortOrder: sortOrder,
    };

    Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === '') delete params[key];
    });

    const { data } = await axios.get('/api/backoffice/passages/paginated', {
        withCredentials: true,
        params,
    });
    return data;
};

const PassageTable = ({ filters }) => {
    const columns = useMemo(
        () => [
            { accessorKey: 'ID passaggio', header: 'ID' },
            { accessorKey: 'Data e ora', header: 'Data e Ora' },
            { accessorKey: 'Direzione', header: 'Direzione' },
            { accessorKey: 'Nome contapassaggi', header: 'Contapassaggi' },
            { accessorKey: 'Nome punto di interesse collegato', header: 'Punto di Interesse' },
            { accessorKey: 'Parco', header: 'Parco' },
        ],
        []
    );

    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [sorting, setSorting] = useState([{ id: 'Data e ora', desc: true }]);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['passages', filters, pagination, sorting],
        queryFn: fetchPaginatedPassages,
        keepPreviousData: true,
    });

    const tableData = data?.data ?? [];
    const pageCount = data?.totalPages ?? 0;

    const table = useReactTable({
        data: tableData,
        columns,
        pageCount,
        state: { pagination, sorting },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualSorting: true,
    });

    if (isError) {
        return <p style={{ color: 'red', padding: '20px' }}>Errore caricamento tabella: {error.message}</p>
    }

    return (
        <div>
            {/* Usa gli stili globali di index.css */}
            <table>
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? ''}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}><LoadingSpinner /></td></tr>
                    ) : table.getRowModel().rows.length === 0 ? (
                        <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px' }}>Nessun dato trovato per i filtri selezionati.</td></tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {/* Controlli di Paginazione */}
            <div className="pagination">
                <div className="pagination-buttons">
                    <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>{'<<'}</button>
                    <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>{'<'}</button>
                    <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{'>'}</button>
                    <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>{'>>'}</button>
                </div>

                <span>
                    Pagina{' '}
                    <strong>
                        {table.getState().pagination.pageIndex + 1} di {Math.max(1, table.getPageCount())}
                    </strong>
                </span>

                <div className="pagination-goto">
                    <span>| Vai a:</span>
                    <input
                        type="number"
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={e => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            table.setPageIndex(page);
                        }}
                    />
                </div>

                <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => { table.setPageSize(Number(e.target.value)); }}
                >
                    {[10, 20, 50, 100].map(pageSize => (
                        <option key={pageSize} value={pageSize}>Mostra {pageSize}</option>
                    ))}
                </select>

                <span style={{ marginLeft: 'auto', marginRight: '10px', marginBottom: '5px', fontWeight: 500 }}>
                    {data?.totalItems ?? 0} risultati totali
                </span>
            </div>
        </div>
    );
};

export default PassageTable;